/**
 * CommandPalette — ⌘K global search.
 * Searches conversations, customers, and knowledge base articles.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MessageSquare, Users, BookOpen, X, ArrowRight } from 'lucide-react';
import { api } from '../../lib/api';
import { cn } from '../../lib/utils';

interface SearchResult {
  id: string;
  type: 'conversation' | 'customer' | 'article';
  title: string;
  subtitle?: string;
  url: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const TYPE_ICON = {
  conversation: MessageSquare,
  customer: Users,
  article: BookOpen,
};

const TYPE_LABEL = {
  conversation: 'Conversation',
  customer: 'Customer',
  article: 'Article',
};

const TYPE_COLOR = {
  conversation: 'text-accent',
  customer: 'text-violet-400',
  article: 'text-emerald-400',
};

export default function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Search debounce
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    const trimmed = query.trim();
    const timeout = setTimeout(async () => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setLoading(true);
      try {
        const { data } = await api.get('/api/search', {
          params: { q: trimmed, limit: 12 },
          signal: abortRef.current.signal,
        });
        const hits: SearchResult[] = [];

        // Conversations
        for (const c of data.conversations ?? []) {
          hits.push({
            id: c.id,
            type: 'conversation',
            title: c.customer?.name || 'Unknown customer',
            subtitle: c.last_message?.content?.slice(0, 80) || c.channel,
            url: `/conversations/${c.id}`,
          });
        }
        // Customers
        for (const c of data.customers ?? []) {
          hits.push({
            id: c.id,
            type: 'customer',
            title: c.name || c.email || 'Unknown',
            subtitle: c.email,
            url: `/customers/${c.id}`,
          });
        }
        // KB articles
        for (const a of data.articles ?? []) {
          hits.push({
            id: a.id,
            type: 'article',
            title: a.title,
            subtitle: a.content?.slice(0, 80),
            url: `/knowledge-base`,
          });
        }

        setResults(hits);
        setActiveIndex(0);
      } catch (err: any) {
        if (err?.code !== 'ERR_CANCELED') setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [query]);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      navigate(result.url);
      onClose();
    },
    [navigate, onClose],
  );

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && results[activeIndex]) {
        handleSelect(results[activeIndex]);
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, results, activeIndex, handleSelect, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-xl mx-4 bg-surface border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border">
          <Search className="w-4 h-4 text-text-3 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search conversations, customers, articles…"
            className="flex-1 bg-transparent text-sm text-text placeholder:text-text-3 focus:outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="text-text-3 hover:text-text-2"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-text-3 bg-surface-2 border border-border rounded">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {loading && (
            <div className="py-8 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!loading && query.trim() && results.length === 0 && (
            <div className="py-8 text-center text-sm text-text-3">
              No results for "<span className="text-text">{query}</span>"
            </div>
          )}

          {!loading && results.length > 0 && (
            <ul className="py-1">
              {results.map((result, i) => {
                const Icon = TYPE_ICON[result.type];
                return (
                  <li key={`${result.type}-${result.id}`}>
                    <button
                      onClick={() => handleSelect(result)}
                      onMouseEnter={() => setActiveIndex(i)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                        i === activeIndex ? 'bg-surface-2' : 'hover:bg-surface-2',
                      )}
                    >
                      <Icon className={cn('w-4 h-4 flex-shrink-0', TYPE_COLOR[result.type])} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text truncate">{result.title}</p>
                        {result.subtitle && (
                          <p className="text-xs text-text-3 truncate">{result.subtitle}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[10px] text-text-3 bg-surface-3 px-1.5 py-0.5 rounded">
                          {TYPE_LABEL[result.type]}
                        </span>
                        {i === activeIndex && (
                          <ArrowRight className="w-3 h-3 text-text-3" />
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {!query.trim() && (
            <div className="py-6 px-4 text-center text-xs text-text-3">
              Type to search conversations, customers, and articles
            </div>
          )}
        </div>

        {/* Footer hint */}
        {results.length > 0 && (
          <div className="px-4 py-2 border-t border-border flex items-center gap-3 text-[10px] text-text-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-surface-2 border border-border rounded">↑↓</kbd> navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-surface-2 border border-border rounded">↵</kbd> open
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-surface-2 border border-border rounded">esc</kbd> close
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
